/**
 * AppUpdateModal
 *
 * Console-style modal for triggering and monitoring an app package update.
 * Follows the DashboardUpdateModal / AptRunnerModal pattern:
 * - Confirmation step with version diff
 * - Dark console area with job progress
 * - Auto-scroll, copy, wrap toggle
 * - Success/failure footer with toast
 */

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { Spinner } from "../ui/Spinner";
import { cn } from "../../utils/cn";
import { toast } from "../../lib/toast";
import { useAppUpdate, type AppUpdateJobStatus } from "../../hooks/useAppUpdate";
import { useConsoleScroll } from "../../hooks/useConsoleScroll";
import { ConsolePanel } from "../system/ConsolePanel";

// Focus trap selector — matches Popover component's implementation
const FOCUSABLE_SELECTOR =
  "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

type AppUpdateModalProps = {
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
  appLabel: string;
  packageName: string;
  installedVersion: string | null;
  latestVersion: string | null;
  /** Whether this app is deprecated (superseded by another). */
  deprecated?: boolean;
  /** The ID of the replacement app when deprecated. */
  replacedBy?: string | null;
  /** The display label of the replacement app (e.g., "Seerr"). */
  replacementLabel?: string | null;
};

const getStatusBadges = (isMigration: boolean): Record<AppUpdateJobStatus, { label: string; className: string }> => ({
  idle: { label: "Ready", className: "bg-muted text-muted-foreground" },
  queued: { label: "Queued", className: "bg-yellow-500/20 text-yellow-400" },
  running: {
    label: isMigration ? "Migrating" : "Updating",
    className: "bg-blue-500/20 text-blue-400 animate-pulse",
  },
  succeeded: { label: "Complete", className: "bg-emerald-500/20 text-emerald-400" },
  failed: { label: "Failed", className: "bg-red-500/20 text-red-400" },
});

export const AppUpdateModal = ({
  open,
  onClose,
  onComplete,
  appLabel,
  packageName,
  installedVersion,
  latestVersion,
  deprecated,
  replacedBy,
  replacementLabel,
}: AppUpdateModalProps) => {
  // Determine if this is a migration (deprecated app with a known replacement)
  const isMigration = Boolean(deprecated && replacedBy);
  const targetLabel = replacementLabel ?? (replacedBy ? replacedBy.charAt(0).toUpperCase() + replacedBy.slice(1) : null);
  const {
    triggerUpdate,
    isUpdating,
    jobStatus,
    jobProgress,
    jobStep,
    jobError,
    jobLogTail,
    reset,
  } = useAppUpdate();

  const [phase, setPhase] = useState<"confirm" | "running" | "done">("confirm");

  const panelRef = useRef<HTMLDivElement>(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const titleId = useId();

  const scroll = useConsoleScroll(jobLogTail.length);

  // Reset when modal opens
  useEffect(() => {
    if (open) {
      reset();
      setPhase("confirm");
    }
  }, [open, reset]);

  // Transition to done when job terminal (guard on phase === "running" to prevent double-fire)
  useEffect(() => {
    if (phase !== "running") return;
    if (jobStatus === "succeeded") {
      setPhase("done");
      if (isMigration && targetLabel) {
        toast.success(`Migration to ${targetLabel} complete`, {
          description: latestVersion ? `Now running ${targetLabel} ${latestVersion}` : undefined,
        });
      } else {
        toast.success(`${appLabel} updated successfully`, {
          description: latestVersion ? `Now running ${latestVersion}` : undefined,
        });
      }
      onCompleteRef.current?.();
    } else if (jobStatus === "failed") {
      setPhase("done");
      toast.error(
        isMigration && targetLabel
          ? `Migration to ${targetLabel} failed`
          : `${appLabel} update failed`,
        { description: jobError ?? "Check logs for details" },
      );
    }
  }, [jobStatus, jobError, appLabel, latestVersion, phase, isMigration, targetLabel]);

  const handleCopyOutput = useCallback(() => {
    scroll.handleCopyOutput(jobLogTail);
  }, [jobLogTail, scroll]);

  const handleStartUpdate = useCallback(async () => {
    setPhase("running");
    await triggerUpdate(packageName);
  }, [triggerUpdate, packageName]);

  const handleClose = useCallback(() => {
    if (isUpdating) return;
    reset();
    onClose();
  }, [isUpdating, reset, onClose]);

  // Escape key handler + focus trap
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        if (!isUpdating) handleClose();
        return;
      }

      if (e.key === "Tab" && panelRef.current) {
        const focusable = Array.from(
          panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;

        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, isUpdating, handleClose]);

  // Focus the panel on open
  useEffect(() => {
    if (open && panelRef.current) {
      const timer = window.setTimeout(() => panelRef.current?.focus(), 0);
      return () => window.clearTimeout(timer);
    }
  }, [open]);

  if (!open) return null;

  const badge = getStatusBadges(isMigration)[jobStatus];
  const isTerminal = jobStatus === "succeeded" || jobStatus === "failed";
  const showConsole = phase !== "confirm";

  const modal = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in-0 duration-200"
        onClick={isTerminal || phase === "confirm" ? handleClose : undefined}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="relative w-full max-w-3xl mx-4 animate-in fade-in-0 zoom-in-95 duration-200 outline-none"
      >
        <div className="rounded-xl border border-white/[0.08] bg-card shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-white/[0.06]">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.04]">
                <Icon icon={isMigration ? "lucide:arrow-right-left" : "lucide:arrow-up-circle"} className="h-4 w-4 text-foreground" />
              </div>
              <div>
                <h2 id={titleId} className="text-sm font-semibold text-foreground">
                  {isMigration && targetLabel
                    ? `Migrate ${appLabel} to ${targetLabel}`
                    : `Update ${appLabel}`}
                </h2>
                <p className="text-[10px] text-muted-foreground/70">System Response</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  "inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium",
                  badge.className
                )}
              >
                {badge.label}
              </span>
              <button
                type="button"
                onClick={handleClose}
                disabled={isUpdating}
                aria-label="Close dialog"
                className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-white/[0.06] transition-colors disabled:opacity-40"
              >
                <Icon icon="lucide:x" className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* Confirmation view */}
          {phase === "confirm" && (
            <div className="px-5 py-6 space-y-4">
              {isMigration && targetLabel ? (
                <p className="text-sm text-foreground">
                  This will migrate your <span className="font-medium">{appLabel}</span> installation
                  to <span className="font-medium">{targetLabel}</span>. Your existing configuration
                  and database will be preserved.
                </p>
              ) : (
                <p className="text-sm text-foreground">
                  Update <span className="font-medium">{appLabel}</span> to the latest version?
                </p>
              )}

              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Installed</span>
                  <code className="rounded bg-muted/60 px-1.5 py-[2px] font-mono text-[10px]">
                    {installedVersion ?? "Unknown"}
                  </code>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">
                    {isMigration && targetLabel ? `${targetLabel} latest` : "Latest"}
                  </span>
                  <code className="rounded bg-amber-500/10 px-1.5 py-[2px] font-mono text-[10px] text-amber-600 dark:text-amber-400">
                    {latestVersion ?? "Unknown"}
                  </code>
                </div>
              </div>

              <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2">
                <Icon icon="lucide:alert-triangle" className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-200/90">
                  {isMigration && targetLabel
                    ? `${appLabel} will be replaced by ${targetLabel}. The service may restart during migration. Active connections could be interrupted briefly.`
                    : "The application service may restart during the update. Active connections could be interrupted briefly."}
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" size="sm" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => void handleStartUpdate()}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <Icon icon={isMigration ? "lucide:arrow-right-left" : "lucide:arrow-up-circle"} className="h-3.5 w-3.5 mr-1.5" />
                  {isMigration && targetLabel ? `Migrate to ${targetLabel}` : `Update ${appLabel}`}
                </Button>
              </div>
            </div>
          )}

          {/* Console output view */}
          {showConsole && (
            <>
              <ConsolePanel
                lines={jobLogTail}
                consoleRef={scroll.consoleRef}
                onScroll={scroll.handleScroll}
                wrapLines={scroll.wrapLines}
                onToggleWrap={() => scroll.setWrapLines((prev) => !prev)}
                autoScroll={scroll.autoScroll}
                onScrollToBottom={scroll.scrollToBottom}
                onCopy={handleCopyOutput}
                progress={jobProgress}
                stepLabel={jobStep}
                isLoading={isUpdating}
              />

              {/* Progress bar for running state */}
              {isUpdating && jobProgress > 0 && (
                <div className="px-5 py-2 border-t border-white/[0.04]">
                  <div className="h-1 w-full rounded-full bg-white/[0.06] overflow-hidden">
                    <div
                      className="h-full rounded-full bg-blue-500 transition-all duration-500"
                      style={{ width: `${jobProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Running footer */}
              {isUpdating && (
                <div className="flex items-center gap-3 px-5 py-3 border-t border-white/[0.06] bg-blue-500/5">
                  <Spinner size="4" className="text-blue-400" />
                  <span className="text-xs text-blue-400">
                    {jobStep ?? (isMigration && targetLabel ? `Migrating to ${targetLabel}...` : "Updating package...")}
                  </span>
                </div>
              )}

              {/* Success footer */}
              {jobStatus === "succeeded" && (
                <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-white/[0.06]">
                  <div className="text-xs text-muted-foreground/70">
                    {isMigration && targetLabel
                      ? `Migration to ${targetLabel} complete.${latestVersion ? ` Now running ${targetLabel} ${latestVersion}.` : ""}`
                      : `Update complete.${latestVersion ? ` Now running ${latestVersion}.` : ""}`}
                  </div>
                  <Button variant="secondary" size="sm" onClick={handleClose}>
                    Close
                  </Button>
                </div>
              )}

              {/* Failed footer */}
              {jobStatus === "failed" && (
                <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-white/[0.06]">
                  <div className="text-xs text-red-400/80 flex-1 min-w-0 truncate">
                    {jobError ?? (isMigration && targetLabel ? `Migration to ${targetLabel} failed.` : "Update failed.")}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={handleCopyOutput}>
                      Copy logs
                    </Button>
                    <Button variant="secondary" size="sm" onClick={handleClose}>
                      Close
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};
